<template>
  <div class="payroll-advances-page">
    <!-- Page Header -->
    <div class="page-header">
      <h1 class="page-title">{{ t('Adiantamentos de Colaboradores') }}</h1>
      <p class="page-description">
        {{ t('Gerencie adiantamentos de colaboradores que serão descontados da folha de pagamento') }}
      </p>
    </div>

    <!-- Action Bar -->
    <div class="action-bar">
      <div class="actions">
        <Button
          data-testid="advances-novo-adiantamento-btn"
          :label="t('Novo Adiantamento')"
          icon="pi pi-plus"
          @click="openNewModal"
        />
      </div>
    </div>

    <!-- Advances Table (always visible) -->
    <EmployeeAdvancesTable
      :advances="advances"
      :can-edit="true"
      :can-delete="true"
      :is-loading="isLoading"
      @edit="handleEdit"
      @view="handleView"
      @cancel="handleCancel"
      @reopen="handleReopen"
      @delete="handleDelete"
      @refresh="loadAdvances"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../../utils/DebugLogger';
import { eventBus } from '../../../eventBus';
import { useEmployeeAdvances } from '../../../composables/useEmployeeAdvances';
import { useConfirmAction } from '../../../composables/useConfirmAction';
import EmployeeAdvancesTable from './EmployeeAdvancesTable.vue';

type Id = number | string;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AdvanceRow = Record<string, any>;

const { t } = useI18n();
const {
  listAdvances,
  cancelAdvance,
  reopenAdvance,
  deleteAdvance,
  isLoading
} = useEmployeeAdvances();
const { confirmAction } = useConfirmAction();

// State
const advances = ref<AdvanceRow[]>([]);

// Methods
const loadAdvances = async (): Promise<void> => {
  log('EmployeeAdvancesPage', 'loadAdvances', 'Loading advances');

  try {
    const data = await listAdvances({});
    advances.value = data as AdvanceRow[];
    log('EmployeeAdvancesPage', 'loadAdvances', 'Loaded advances', { count: data.length });
  } catch (err) {
    log('EmployeeAdvancesPage', 'loadAdvances', 'Error', err);
    eventBus.emit('globalMessage.error', {
      message: t('Erro ao carregar adiantamentos'),
      duration: 5000
    });
  }
};

const openNewModal = (): void => {
  log('EmployeeAdvancesPage', 'openNewModal', 'Opening new modal');
  eventBus.emit('modal.employee-advances.open', null);
};

const handleEdit = (advance: AdvanceRow): void => {
  log('EmployeeAdvancesPage', 'handleEdit', 'Editing advance', advance.id);
  eventBus.emit('modal.employee-advances.open', advance);
};

const handleView = (advance: AdvanceRow): void => {
  log('EmployeeAdvancesPage', 'handleView', 'Viewing advance', advance.id);
  eventBus.emit('modal.employee-advances.open', advance);
};

const handleCancel = (advance: AdvanceRow): void => {
  log('EmployeeAdvancesPage', 'handleCancel', 'Canceling advance', advance.id);

  confirmAction({
    message: t('Tem certeza que deseja cancelar este adiantamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await cancelAdvance(advance.id);
      if (success) {
        eventBus.emit('globalMessage.success', {
          message: t('Adiantamento cancelado com sucesso'),
          duration: 3000
        });
        loadAdvances();
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao cancelar adiantamento'),
          duration: 5000
        });
      }
    }
  });
};

const handleReopen = (advance: AdvanceRow): void => {
  log('EmployeeAdvancesPage', 'handleReopen', 'Reopening advance', advance.id);

  confirmAction({
    message: t('Tem certeza que deseja reabrir este adiantamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await reopenAdvance(advance.id);
      if (success) {
        eventBus.emit('globalMessage.success', {
          message: t('Adiantamento reaberto com sucesso'),
          duration: 3000
        });
        loadAdvances();
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao reabrir adiantamento'),
          duration: 5000
        });
      }
    }
  });
};

const handleDelete = (advance: AdvanceRow): void => {
  log('EmployeeAdvancesPage', 'handleDelete', 'Deleting advance', advance.id);

  confirmAction({
    message: t('Tem certeza que deseja excluir este adiantamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      const success = await deleteAdvance(advance.id);
      if (success) {
        eventBus.emit('globalMessage.success', {
          message: t('Adiantamento excluído com sucesso'),
          duration: 3000
        });
        loadAdvances();
      } else {
        eventBus.emit('globalMessage.error', {
          message: t('Erro ao excluir adiantamento'),
          duration: 5000
        });
      }
    }
  });
};

// Lifecycle
onMounted(() => {
  loadAdvances();

  // Listen for refresh events
  eventBus.on('employee-advances.refresh', loadAdvances);
  eventBus.on('employee-advances.created', loadAdvances);
  eventBus.on('employee-advances.updated', loadAdvances);
});

onBeforeUnmount(() => {
  eventBus.off('employee-advances.refresh', loadAdvances);
  eventBus.off('employee-advances.created', loadAdvances);
  eventBus.off('employee-advances.updated', loadAdvances);
});
</script>

<style scoped>
.payroll-advances-page {
  padding: 1.5rem;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 0.5rem 0;
}

.page-description {
  color: var(--text-color-secondary);
  margin: 0;
}

.action-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}
</style>
